/**
 * Insurance API response parser
 * Transform API responses to UI-friendly format for Insurance Overview
 */

import { formatIndianNumberWithDecimals } from "../../utils/currency.utils";

const formatRupee = (val) => {
    if (val === null || val === undefined || val === "" || String(val).trim() === "") return "-";
    const num = Number(String(val).replace(/,/g, ''));
    if (isNaN(num)) return "-";
    return `₹${formatIndianNumberWithDecimals(num)}`;
};

const normalizeNullValue = (value) => {
    if (value === null || value === undefined || value === "N/A" || value === "null" || String(value).trim() === "" || String(value) === "Invalid Date") {
        return "-";
    }
    return value;
};

const formatAsOnDate = (isoString) => {
    if (!isoString) return "-";
    try {
        const date = new Date(isoString);
        return date.toLocaleString("en-IN", {
            day: "2-digit",
            month: "long",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
            timeZone: "Asia/Kolkata",
        }).replace(",", ";") + " IST";
    } catch {
        return "-";
    }
};

/**
 * Parser for insurance overview API.
 * 
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed insurance data structure for UI
 */
export const parseInsuranceOverview = (response) => {
    if (!response || !response.result) {
        return { 
            policies: [],
            success: false,
            message: response?.message || "Invalid insurance data"
        };
    }

    const { result } = response;
    const policies = [];
    const asOfDateString = result?.asOnDate ? `as on date ${formatAsOnDate(result.asOnDate)}` : "";

    // Categories to skip from iterations
    const skipKeys = ["asOnDate"];

    Object.keys(result).forEach(key => {
        if (skipKeys.includes(key)) return;

        const categoryData = result[key];
        if (!categoryData || !categoryData.policies) return;

        const categoryName = key.charAt(0).toUpperCase() + key.slice(1) + " Insurance";
        
        policies.push({
            category: categoryName,
            asOfDate: asOfDateString,
            totalSumAssured: formatRupee(categoryData?.totalSumAssured),
            totalPremium: formatRupee(categoryData?.totalPremiumValue),
            items: (categoryData?.policies || []).map((policy, idx) => {
                const isLife = key.toLowerCase() === "life";
                
                const fields = isLife ? [
                    { 
                        label: "Premium Amount", 
                        value: formatRupee(policy?.premiumAmount) 
                    },
                    { 
                        label: "Sum Assured", 
                        value: formatRupee(policy?.sumAssured) 
                    },
                    { 
                        label: "Term", 
                        value: normalizeNullValue(policy?.term) 
                    }
                ] : [
                    { 
                        label: "Premium Amount", 
                        value: formatRupee(policy?.premiumAmount) 
                    },
                    { 
                        label: "Sum Assured", 
                        value: formatRupee(policy?.sumAssured) 
                    },
                    { 
                        label: "Next Due Date", 
                        value: normalizeNullValue(policy?.nextDueDate) 
                    },
                    { 
                        label: "Type", 
                        value: normalizeNullValue(policy?.type) 
                    }
                ];

                return {
                    id: policy?.policyNumber || `${key}-${idx}`,
                    name: normalizeNullValue(policy?.name),
                    status: (policy?.status || "ACTIVE").toUpperCase(),
                    folio: policy?.policyNumber || "-",
                    type: null,
                    fields
                };
            })
        });
    });

    return { 
        policies,
        success: true,
        message: response?.message || "Success"
    };
};
